/**
 * Persistent call bar.
 *
 * Rendered by the shell, outside the router outlet, so it survives route changes
 * exactly as an agent expects when they navigate away mid-call.
 */

import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Badge, Button, STATE_TONE } from '@/ui/primitives';
import { formatDuration, secondsSince } from '@/lib/format';
import type { AgentStateResponse, CallDetail } from '@/lib/api/types';
import { controlAvailability } from './callControls';
import type { ControlContext } from './callControls';

const TERMINAL_CALL_STATUSES = new Set([
  'completed',
  'abandoned',
  'failed',
  'busy',
  'no_answer',
  'voicemail',
  'canceled',
]);

export function isTerminalCallStatus(status: string): boolean {
  return TERMINAL_CALL_STATUSES.has(status);
}

export function displayedTalkSeconds(call: CallDetail | null, now: number): number {
  if (!call) return 0;
  if (isTerminalCallStatus(call.status)) return call.timers.talk_seconds;
  return call.answered_at ? secondsSince(call.answered_at, now) : 0;
}

interface CallBarProps {
  agent: AgentStateResponse | null;
  call: CallDetail | null;
  controlContext: ControlContext;
  connection: string;
  onAnswer: () => void;
  onHold: () => void;
  onResume: () => void;
  onHangup: () => void;
  onMute: (muted: boolean) => void;
  onCompleteAcw: () => void;
}

export function CallBar({
  agent,
  call,
  controlContext,
  connection,
  onAnswer,
  onHold,
  onResume,
  onHangup,
  onMute,
  onCompleteAcw,
}: CallBarProps) {
  const [now, setNow] = useState(() => Date.now());
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const agentLeg = call?.legs.find((leg) => leg.role === 'agent');
    setMuted(Boolean(agentLeg?.is_muted));
  }, [call]);

  const state = agent?.state ?? 'offline';
  const connectionLabel =
    connection === 'online'
      ? 'Connected'
      : connection === 'offline'
        ? 'Offline'
        : connection === 'reconnecting'
          ? 'Reconnecting…'
          : 'Connecting…';
  const stateSeconds = agent?.state_changed_at
    ? Math.max(
        agent.seconds_in_state ?? 0,
        Math.floor((now - new Date(agent.state_changed_at).getTime()) / 1000),
      )
    : agent?.seconds_in_state ?? 0;
  const customerNumber = call?.direction === 'outbound' ? call.to_e164 : call?.from_e164;
  const callFinished = Boolean(call && isTerminalCallStatus(call.status));
  const talkSeconds = displayedTalkSeconds(call, now);
  const acwRemaining = agent?.acw_seconds_remaining ?? null;

  const can = (id: Parameters<typeof controlAvailability>[0]) =>
    controlAvailability(id, controlContext);

  return (
    <footer
      className="flex min-h-bar shrink-0 flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-950 px-2 py-2 text-white shadow-[0_-8px_24px_rgba(15,23,42,0.16)] sm:h-bar sm:flex-nowrap sm:gap-3 sm:px-3 sm:py-0"
      aria-label="Call controls"
    >
      <div className="flex min-w-0 flex-1 flex-col sm:min-w-[190px] sm:flex-none">
        <div className="flex items-center gap-1.5">
          <Badge tone={STATE_TONE[state] ?? 'neutral'}>{state.replace('_', ' ')}</Badge>
          <span className="knx-num text-xs text-slate-300">
            {formatDuration(stateSeconds)}
          </span>
        </div>
        <span className="truncate text-xs text-slate-300">
          {callFinished ? 'Call ended - complete wrap-up' : agent?.state_reason ?? (call ? 'Live conversation' : 'No active conversation')}
        </span>
      </div>

      {call ? (
        <div className="order-2 flex w-full items-center justify-between gap-2 border-t border-white/15 pt-2 sm:order-none sm:w-auto sm:justify-start sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0">
          <div className="flex flex-col">
            <span className="knx-num text-md font-semibold text-white">{customerNumber}</span>
            <span className="text-2xs uppercase tracking-wide text-slate-300">
              {call.direction} - {call.status.replace('_', ' ')}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Timer label="Talk" value={formatDuration(talkSeconds)} />
            <Timer label="Hold" value={formatDuration(call.timers.hold_seconds)} />
            <Timer label="Queue" value={formatDuration(call.timers.queue_seconds)} />
          </div>
        </div>
      ) : null}

      {state === 'acw' ? (
        <div className="flex items-center gap-2 border-l border-white/15 pl-3">
          <span className="text-2xs uppercase tracking-wide text-slate-300">Wrap-up</span>
          <span
            className={clsx(
              'knx-num text-md',
              agent?.acw_overrun ? 'text-danger' : acwRemaining != null && acwRemaining <= 10 ? 'text-warn' : 'text-fg',
            )}
          >
            {formatDuration(acwRemaining)}
          </span>
          {agent?.acw_overrun ? <Badge tone="danger">Overrun</Badge> : null}
        </div>
      ) : null}

      <div className="order-3 ml-0 flex w-full items-center gap-1.5 overflow-x-auto sm:order-none sm:ml-auto sm:w-auto">
        {call?.direction !== 'outbound' && state === 'ringing' ? (
          <Control id="answer" label="Answer" shortcut="F1" tone="ok" onClick={onAnswer} availability={can('answer')} />
        ) : null}
        {state === 'on_call' || state === 'hold' ? (
          <>
            <Control
              id="hold"
              label={state === 'hold' ? 'Resume' : 'Hold'}
              shortcut="F2"
              onClick={state === 'hold' ? onResume : onHold}
              availability={state === 'hold' ? can('resume') : can('hold')}
            />
            <Control
              id="mute"
              label={muted ? 'Unmute' : 'Mute'}
              shortcut="F3"
              onClick={() => onMute(!muted)}
              availability={can('mute')}
            />
          </>
        ) : null}
        {call && ['ringing', 'on_call', 'hold'].includes(state) ? (
          <Control
            id="hangup"
            label={state === 'ringing' ? 'Cancel call' : 'Hang up'}
            shortcut="F4"
            tone="danger"
            onClick={onHangup}
            availability={can('hangup')}
          />
        ) : null}
        {state === 'acw' ? (
          <Control
            id="complete_acw"
            label="Finish wrap-up"
            shortcut="F8"
            tone="brand"
            onClick={onCompleteAcw}
            availability={can('complete_acw')}
          />
        ) : null}
        {!call && state !== 'acw' ? (
          <span className="px-2 text-xs font-medium text-slate-300">
            {state === 'available' ? 'Ready for the next call' : `On ${state.replace('_', ' ')}`}
          </span>
        ) : null}
        <span
          className={clsx(
            'ml-2 inline-flex items-center gap-1 text-2xs uppercase tracking-wide',
            connection === 'online' ? 'text-emerald-300' : connection === 'offline' ? 'text-red-300' : 'text-amber-300',
          )}
          role="status"
        >
          <span
            className={clsx(
              'h-1.5 w-1.5 rounded-full',
              connection === 'online' ? 'bg-emerald-400' : connection === 'offline' ? 'bg-red-400' : 'animate-knx-pulse-dot bg-amber-400',
            )}
          />
          {connectionLabel}
        </span>
      </div>
    </footer>
  );
}

function Timer({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-2xs uppercase tracking-wide text-slate-400">{label}</span>
      <span className="knx-num text-base font-semibold text-white">{value}</span>
    </div>
  );
}

function Control({
  id,
  label,
  shortcut,
  tone,
  onClick,
  availability,
}: {
  id: string;
  label: string;
  shortcut: string;
  tone?: 'ok' | 'danger' | 'brand';
  onClick: () => void;
  availability: { enabled: boolean; reason?: string };
}) {
  return (
    <Button
      size="sm"
      data-control={id}
      tone={tone ?? 'neutral'}
      variant={tone ? 'solid' : 'outline'}
      shortcut={shortcut}
      disabled={!availability.enabled}
      title={availability.enabled ? label : availability.reason}
      aria-label={availability.enabled ? label : `${label} unavailable: ${availability.reason}`}
      className="disabled:border-white/15 disabled:bg-white/5 disabled:text-slate-400 disabled:opacity-100"
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
